import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import type { DocumentFraudScoreDto } from '../../generated/kubb';
import { useKorintConfig } from './useKorintConfig';

export interface ExtractionResult {
  status: 'PENDING' | 'DONE' | 'FAILED' | 'SKIPPED';
  fields?: Record<string, unknown>;
  aiOperationId?: string;
}

export type ValidityRuleType = 'READABLE' | 'REQUIRED_FIELDS' | 'PROMPT' | 'FIELD_VALIDATION';

export interface ValidityRuleResult {
  ruleType: ValidityRuleType;
  passed: boolean;
  message?: string;
  metadata?: Record<string, unknown>;
}

export interface AIValidityResult {
  documentId: string;
  status: 'VALID' | 'INVALID' | 'FAILED' | 'SKIPPED';
  results: ValidityRuleResult[];
  aiMetadata?: {
    aiOperationId?: string;
    promptSlug?: string;
    model?: string;
    durationMs?: number;
  };
  override?: {
    overriddenBy: string;
    overriddenAt: string;
    previousStatus: string;
    originalStatus?: string;
  };
}

export interface ClassifyResult {
  category: string;
  reasoning: string;
  qualityFlags: string[];
  side?: string;
  judgements: {
    consensus: boolean;
    verdicts: Array<{ judge: string; agrees: boolean; reasoning: string }>;
  };
  traceId: string;
}

export interface ClassificationError {
  code: string;
  message: string;
}

export type ClassificationStatus =
  | 'idle'
  | 'validating'
  | 'validated'
  | 'scanning'
  | 'scan_complete'
  | 'scan_infected'
  | 'scan_timeout'
  | 'classifying'
  | 'classified'
  | 'extracting'
  | 'checking_validity'
  | 'validity_checked'
  | 'checking_fraud'
  | 'completed'
  | 'error';

export interface DocumentClassificationState {
  documentId: string;
  status: ClassificationStatus;
  result: ClassifyResult | null;
  extraction: ExtractionResult | null;
  validity: AIValidityResult | null;
  /** Only streamed to users allowed to see policy document fraud results. */
  fraud?: DocumentFraudScoreDto;
  error: ClassificationError | null;
}

export interface UseDocumentsAutoClassificationOptions {
  onDocumentValidated?: (documentId: string) => void;
  onDocumentClassified?: (
    documentId: string,
    result: ClassifyResult,
    extraction: ExtractionResult | null,
  ) => void;
  onDocumentValidityChecked?: (documentId: string, validity: AIValidityResult) => void;
  onDocumentError?: (documentId: string, error: ClassificationError) => void;
  onAllComplete?: () => void;
  getAccessToken?: () => string | undefined;
}

export interface ProcessDocumentsOptions {
  /**
   * When true, the SSE call sets ?force=true so the backend bypasses
   * FILE_ALREADY_UPLOADED on an existing document and re-runs the AI
   * pipeline (classify/extract/validity) without re-publishing
   * document_uploaded events. Use for the user-triggered retry path.
   */
  force?: boolean;
}

export interface UseDocumentsAutoClassificationResult {
  classificationStates: Map<string, DocumentClassificationState>;
  lastActivityAt: number;
  processDocuments: (keys: string[], options?: ProcessDocumentsOptions) => Promise<void>;
  cancelAll: () => void;
  dismissDocument: (documentId: string) => void;
  isProcessing: boolean;
  getDocumentStatus: (documentId: string) => DocumentClassificationState | undefined;
}

// Only delay on statuses that start a new visible phase to avoid stacking delays.
const VISIBLE_STATUSES = new Set([
  'validating',
  'classifying',
  'extracting',
  'checking_validity',
  'checking_fraud',
]);
const MIN_DISPLAY_MS = 600;

const TERMINAL_STATUSES = new Set<ClassificationStatus>([
  'completed',
  'error',
  'scan_infected',
  'scan_timeout',
]);

// Covers scanning, classification, extraction, prefill, and validity on the same stream.
const REQUEST_TIMEOUT_MS = 10 * 60_000;

export function useDocumentsAutoClassification({
  onDocumentValidated,
  onDocumentClassified,
  onDocumentValidityChecked,
  onDocumentError,
  onAllComplete,
  getAccessToken,
}: UseDocumentsAutoClassificationOptions): UseDocumentsAutoClassificationResult {
  const { clientOptions } = useKorintConfig();
  // Only used by single-tenant apps, where tenant is always set.
  const { baseUrl, tenant = '' } = clientOptions;

  // SSE must go through the dedicated stream ALB (stream.<env>.korint.io); the api.<env>
  // API Gateway path buffers responses and caps integrations at ~30s, which document
  // validation (Bedrock classify+extract) routinely exceeds. See terraform/alb.tf.
  const streamBaseUrl = import.meta.env.VITE_KORINT_STREAM_URL || baseUrl;

  const [classificationStates, setClassificationStates] = useState<
    Map<string, DocumentClassificationState>
  >(new Map());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // biome-ignore lint/correctness/useExhaustiveDependencies: cleanup on unmount only
  useEffect(() => {
    const controllers = abortControllersRef.current;
    return () => {
      controllers.forEach(controller => controller.abort());
      controllers.clear();
    };
  }, []);

  const [pendingCount, setPendingCount] = useState(0);
  const [lastActivityAt, setLastActivityAt] = useState(Date.now);

  const processDocument = useCallback(
    async (key: string, token: string, force?: boolean) => {
      setLastActivityAt(Date.now());
      setClassificationStates(prev => {
        const newMap = new Map(prev);
        newMap.set(key, {
          documentId: key,
          status: 'validating',
          result: null,
          extraction: null,
          validity: null,
          error: null,
        });
        return newMap;
      });

      const url = new URL(`${streamBaseUrl}/documents/validate`);
      url.searchParams.set('key', key);
      if (force) url.searchParams.set('force', 'true');

      const abortController = new AbortController();
      abortControllersRef.current.set(key, abortController);

      // No AbortSignal.timeout/any: they need Safari 16 / 17.4
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        abortController.abort();
      }, REQUEST_TIMEOUT_MS);

      let resolvedDocumentId = key;

      try {
        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            tenant: tenant,
            Accept: 'text/event-stream',
          },
          signal: abortController.signal,
        });

        if (!response.ok) {
          const httpError = new Error(`HTTP ${response.status}: ${response.statusText}`);
          (httpError as Error & { httpStatus?: number }).httpStatus = response.status;
          throw httpError;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body available');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let receivedTerminalStatus = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          setLastActivityAt(Date.now());
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data:')) {
              const jsonStr = line.slice(5).trim();
              if (jsonStr) {
                try {
                  const event = JSON.parse(jsonStr);

                  // Skip keepalive events
                  if (event.keepalive) continue;
                  if (TERMINAL_STATUSES.has(event.status)) receivedTerminalStatus = true;

                  const updateState = (update: Partial<DocumentClassificationState>) => {
                    setClassificationStates(prev => {
                      const newMap = new Map(prev);
                      const existing = prev.get(resolvedDocumentId);
                      newMap.set(resolvedDocumentId, {
                        documentId: resolvedDocumentId,
                        status: existing?.status ?? 'idle',
                        result: existing?.result ?? null,
                        extraction: existing?.extraction ?? null,
                        validity: existing?.validity ?? null,
                        error: existing?.error ?? null,
                        ...update,
                      });
                      return newMap;
                    });
                  };

                  switch (event.status) {
                    case 'validating': {
                      if (event.documentId) {
                        resolvedDocumentId = event.documentId;
                        setClassificationStates(prev => {
                          const newMap = new Map(prev);
                          const existing = prev.get(key);
                          newMap.delete(key);
                          newMap.set(resolvedDocumentId, {
                            documentId: resolvedDocumentId,
                            status: 'validating',
                            result: existing?.result ?? null,
                            extraction: existing?.extraction ?? null,
                            validity: existing?.validity ?? null,
                            error: existing?.error ?? null,
                          });
                          return newMap;
                        });
                        const ctrl = abortControllersRef.current.get(key);
                        if (ctrl) {
                          abortControllersRef.current.delete(key);
                          abortControllersRef.current.set(resolvedDocumentId, ctrl);
                        }
                        onDocumentValidated?.(resolvedDocumentId);
                      } else {
                        updateState({ status: 'validating' });
                      }
                      break;
                    }
                    case 'scanning':
                      updateState({ status: 'scanning' });
                      break;
                    case 'scan_complete':
                      updateState({ status: 'scan_complete' });
                      break;
                    case 'scan_infected':
                      updateState({ status: 'scan_infected' });
                      break;
                    case 'scan_timeout':
                      updateState({ status: 'scan_timeout' });
                      break;
                    case 'classifying':
                      updateState({ status: 'classifying' });
                      break;
                    case 'classified':
                      updateState({
                        status: 'classified',
                        result: event.result ?? null,
                      });
                      break;
                    case 'extracting':
                      updateState({ status: 'extracting' });
                      break;
                    case 'checking_validity':
                      updateState({ status: 'checking_validity' });
                      break;
                    case 'validity_checked': {
                      const validityResult = event.validity ?? null;
                      updateState({
                        status: 'validity_checked',
                        validity: validityResult,
                      });
                      if (validityResult) {
                        onDocumentValidityChecked?.(resolvedDocumentId, validityResult);
                      }
                      break;
                    }
                    case 'checking_fraud':
                      updateState({ status: 'checking_fraud' });
                      break;
                    case 'completed': {
                      const extraction: ExtractionResult | null = event.extraction ?? null;
                      const update: Partial<DocumentClassificationState> = {
                        status: 'completed',
                        extraction,
                      };
                      if (event.result) {
                        update.result = event.result;
                      }
                      if (event.validity) {
                        update.validity = event.validity;
                      }
                      if (event.fraud) {
                        update.fraud = event.fraud;
                      }
                      updateState(update);
                      if (event.result) {
                        onDocumentClassified?.(resolvedDocumentId, event.result, extraction);
                      }
                      break;
                    }
                    case 'error':
                      updateState({
                        status: 'error',
                        error: event.error ?? null,
                      });
                      if (event.error) {
                        onDocumentError?.(resolvedDocumentId, event.error);
                      }
                      break;
                  }

                  // Ensure each visible status is rendered for a minimum duration.
                  // Without this, React batches rapid state updates from the same
                  // SSE chunk and only renders the final state.
                  if (VISIBLE_STATUSES.has(event.status)) {
                    await new Promise(resolve => setTimeout(resolve, MIN_DISPLAY_MS));
                  }
                } catch {
                  // Ignore JSON parse errors for incomplete data
                }
              }
            }
          }
        }

        if (!receivedTerminalStatus) {
          throw new Error('Stream closed before document processing completed');
        }
      } catch (err) {
        // User-triggered cancel: stay silent. Timeout: surface as REQUEST_TIMEOUT.
        if (abortController.signal.aborted && !timedOut) {
          return;
        }

        const httpStatus = (err as Error & { httpStatus?: number }).httpStatus;
        const isServiceUnavailable = httpStatus === 502 || httpStatus === 503 || httpStatus === 504;

        const classificationError: ClassificationError = timedOut
          ? {
              code: 'REQUEST_TIMEOUT',
              message: 'Classification request timed out',
            }
          : isServiceUnavailable
            ? {
                code: 'SERVICE_UNAVAILABLE',
                message: 'Classification service temporarily unavailable',
              }
            : {
                code: 'NETWORK_ERROR',
                message: (err as Error).message,
              };

        setClassificationStates(prev => {
          const newMap = new Map(prev);
          newMap.set(resolvedDocumentId, {
            documentId: resolvedDocumentId,
            status: 'error',
            result: null,
            extraction: null,
            validity: null,
            error: classificationError,
          });
          return newMap;
        });
        onDocumentError?.(resolvedDocumentId, classificationError);
      } finally {
        clearTimeout(timeoutId);
        // If abortController was already cleaned up by cancelAll, skip decrement
        if (abortControllersRef.current.has(resolvedDocumentId)) {
          abortControllersRef.current.delete(resolvedDocumentId);
          setPendingCount(prev => {
            const next = prev - 1;
            if (next === 0) onAllComplete?.();
            return next;
          });
        }
      }
    },
    [
      streamBaseUrl,
      tenant,
      onDocumentValidated,
      onDocumentClassified,
      onDocumentValidityChecked,
      onDocumentError,
      onAllComplete,
    ],
  );

  const processDocuments = useCallback(
    async (keys: string[], options?: ProcessDocumentsOptions) => {
      if (keys.length === 0) return;

      try {
        let token: string | undefined;
        try {
          const session = await fetchAuthSession();
          token = session.tokens?.accessToken.toString();
        } catch {
          // Unauthenticated users (e.g. anonymous sessions) will throw
        }

        if (!token) {
          token = getAccessToken?.();
        }

        if (!token) {
          const authError: ClassificationError = {
            code: 'AUTH_ERROR',
            message: 'No authentication token available',
          };

          keys.forEach(key => {
            setClassificationStates(prev => {
              const newMap = new Map(prev);
              newMap.set(key, {
                documentId: key,
                status: 'error',
                result: null,
                extraction: null,
                validity: null,
                error: authError,
              });
              return newMap;
            });
            onDocumentError?.(key, authError);
          });
          return;
        }

        setPendingCount(keys.length);

        await Promise.all(keys.map(key => processDocument(key, token, options?.force)));
      } catch (err) {
        const error: ClassificationError = {
          code: 'AUTH_ERROR',
          message: (err as Error).message,
        };

        keys.forEach(key => {
          setClassificationStates(prev => {
            const newMap = new Map(prev);
            newMap.set(key, {
              documentId: key,
              status: 'error',
              result: null,
              extraction: null,
              validity: null,
              error,
            });
            return newMap;
          });
          onDocumentError?.(key, error);
        });
      }
    },
    [processDocument, onDocumentError, getAccessToken],
  );

  const dismissDocument = useCallback((documentId: string) => {
    const controller = abortControllersRef.current.get(documentId);
    const wasPending = controller !== undefined;
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(documentId);
    }

    setClassificationStates(prev => {
      if (!prev.has(documentId)) return prev;
      const newMap = new Map(prev);
      newMap.delete(documentId);
      return newMap;
    });

    if (wasPending) {
      setPendingCount(prev => Math.max(0, prev - 1));
    }
  }, []);

  const cancelAll = useCallback(() => {
    abortControllersRef.current.forEach(controller => controller.abort());
    abortControllersRef.current.clear();
    setPendingCount(0);

    setClassificationStates(prev => {
      const newMap = new Map(prev);
      newMap.forEach((state, key) => {
        if (state.status !== 'completed' && state.status !== 'error') {
          newMap.set(key, {
            ...state,
            status: 'idle',
            result: null,
            extraction: null,
            validity: null,
            error: null,
          });
        }
      });
      return newMap;
    });
  }, []);

  const getDocumentStatus = useCallback(
    (documentId: string) => classificationStates.get(documentId),
    [classificationStates],
  );

  const isProcessing = pendingCount > 0;

  return {
    classificationStates,
    lastActivityAt,
    processDocuments,
    cancelAll,
    dismissDocument,
    isProcessing,
    getDocumentStatus,
  };
}
